import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Checkbox } from "../ui/checkbox";
import { Loader2, Download } from "lucide-react";
import { apiErr } from "../../lib/format";

export default function LeadCaptureModal({ open, onOpenChange, onSubmit }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", marketing_consent: false });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) { setError("Name and email are required."); return; }
    setLoading(true); setError("");
    try {
      await onSubmit(form);
      onOpenChange(false);
    } catch (err) {
      setError(apiErr(err.response?.data?.detail) || "Could not generate your report.");
    } finally { setLoading(false); }
  };

  const field = "w-full rounded-lg border border-brand-hairline px-4 py-2.5 text-sm outline-none focus:ring-1 focus:ring-brand-emerald";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-brand-cream" data-testid="pdf-lead-capture-form">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-brand-ink">Get your full PDF report</DialogTitle>
          <DialogDescription className="text-brand-stone">
            Your branded valuation, comparable sales, and seller net sheet — delivered as a downloadable PDF.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div>
            <label className="text-sm font-medium text-brand-ink" htmlFor="lead-name">Name *</label>
            <input id="lead-name" data-testid="lead-name-input" className={field} value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <label className="text-sm font-medium text-brand-ink" htmlFor="lead-email">Email *</label>
            <input id="lead-email" data-testid="lead-email-input" type="email" className={field} value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          <div>
            <label className="text-sm font-medium text-brand-ink" htmlFor="lead-phone">Phone (optional)</label>
            <input id="lead-phone" data-testid="lead-phone-input" className={field} value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </div>
          <label className="flex items-start gap-3 rounded-lg bg-brand-sand p-3 cursor-pointer">
            <Checkbox checked={form.marketing_consent} onCheckedChange={(v) => setForm({ ...form, marketing_consent: !!v })}
              className="mt-0.5" data-testid="marketing-consent-checkbox" />
            <span className="text-xs text-brand-stone leading-relaxed">
              I'd like to receive occasional market updates and selling tips from Equitas Homes. (Optional — requesting the report does not enroll you in marketing.)
            </span>
          </label>
          {error && <p className="text-sm text-destructive" data-testid="lead-error">{error}</p>}
          <button type="submit" disabled={loading} data-testid="download-pdf-report-button"
            className="w-full flex items-center justify-center gap-2 rounded-xl bg-brand-emerald px-6 py-3.5 font-semibold text-brand-cream hover:bg-brand-emeraldHover disabled:opacity-60 transition-colors">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            {loading ? "Generating…" : "Generate & Download Report"}
          </button>
          <p className="text-[0.7rem] text-brand-stone/70 text-center">Your download link is private and expires in 7 days.</p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
